import { Eye, InfoCircle, MoreVertical, Send2 } from '@/components/icons';
import { InteractiveCard } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { parseInstrumentName } from '@/lib/instrument-name';
import InstrumentCardBanner from './InstrumentCardBanner';
import type { Test } from '@/api/tests';
import type { KeyboardEvent, MouseEvent } from 'react';

interface InstrumentCardProps {
    test: Test;
    onOpen: (test: Test) => void;
    onAssign: (test: Test) => void;
    onPreview: (test: Test) => void;
}

/**
 * The card's practical selection criteria, ordered by how often they decide the
 * pick: how long this will take the patient, then who it is for.
 *
 * These used to carry an icon each and stack vertically, which cost a third of
 * the card's height to say eight words — and the icons were redundant besides,
 * since the labels already read "itens" and "min". Audience comes last because
 * it is the longest and the one worth losing when the line has to truncate.
 */
function instrumentFacts(test: Test): string[] {
    const facts: string[] = [];

    if (test.question_count) facts.push(`${test.question_count} itens`);
    if (test.estimated_minutes) facts.push(`~${test.estimated_minutes} min`);

    const audience = test.target_audience?.trim();
    if (audience) facts.push(audience);

    return facts;
}

export default function InstrumentCard({ test, onOpen, onAssign, onPreview }: InstrumentCardProps) {
    const { acronym, shortName } = parseInstrumentName(test.name, test.test_id);
    const facts = instrumentFacts(test);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onOpen(test);
    };

    return (
        <InteractiveCard
            interactive
            hoverable
            role="button"
            tabIndex={0}
            className="group"
            onClick={() => onOpen(test)}
            onKeyDown={handleKeyDown}
        >
            <CardContent className="flex h-full flex-col gap-0 p-0">
                <InstrumentCardBanner test={test} acronym={acronym} />

                <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3">
                    <p className="line-clamp-2 text-pretty text-sm font-medium leading-6 text-foreground">
                        {shortName}
                    </p>

                    {facts.length > 0 ? (
                        /* Separated by a spaced middle dot rather than by markup:
                           screen readers skip the dot as punctuation, and the
                           surrounding spaces are what keep the facts from running
                           into each other when it is skipped. */
                        <p className="truncate text-xs tabular-nums text-muted-foreground">
                            {facts.join(' · ')}
                        </p>
                    ) : null}

                    <div className="mt-auto flex items-center gap-2 pt-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="surface"
                            className="h-9 flex-1 justify-center border-brand/10 text-brand hover:bg-brand-subtle/60 hover:text-brand"
                            onClick={(event) => {
                                event.stopPropagation();
                                onAssign(test);
                            }}
                        >
                            <Send2 className="size-4" />
                            Enviar
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    size="iconSm"
                                    variant="ghost"
                                    className="size-9 text-muted-foreground opacity-70 hover:text-foreground hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
                                    aria-label={`Mais ações para ${acronym}`}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <MoreVertical className="size-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                onClick={(event: MouseEvent) => event.stopPropagation()}
                            >
                                <DropdownMenuItem
                                    className="gap-2"
                                    onSelect={() => onPreview(test)}
                                >
                                    <Eye className="size-4" />
                                    Visualizar
                                </DropdownMenuItem>
                                <DropdownMenuItem className="gap-2" onSelect={() => onOpen(test)}>
                                    <InfoCircle className="size-4" />
                                    Ver detalhes
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </CardContent>
        </InteractiveCard>
    );
}

export function InstrumentCardSkeleton() {
    return (
        <Card className="h-full overflow-hidden">
            <CardContent className="flex h-full flex-col gap-0 p-0">
                <Skeleton className="h-16 shrink-0 rounded-none" />
                <div className="flex flex-1 flex-col gap-1.5 p-3">
                    <div className="space-y-1.5">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                    <Skeleton className="h-3.5 w-40" />
                    <div className="mt-auto flex items-center gap-2 pt-2">
                        <Skeleton className="h-9 flex-1" />
                        <Skeleton className="size-9" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
